
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import {
    useGetEmailDomain,
    useAddEmailDomain,
    useCheckEmailDomain,
    useDeleteEmailDomain,
} from '@/lib/api/emailDomains'
import { BackButton } from '@/components/ui/BackButton'

const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/

const emailDomainSchema = z
    .object({
        domain: z.string().min(1, 'Domain is required').regex(DOMAIN_REGEX, 'Invalid domain format'),
        fromEmail: z.string().min(1, 'From email is required').email('Invalid email format'),
    })
    .refine((data) => data.fromEmail.endsWith(`@${data.domain}`), {
        message: 'From email must use the custom domain',
        path: ['fromEmail'],
    })

type FormValues = z.infer<typeof emailDomainSchema>

interface DNSRecord {
    record: string
    name: string
    type: string
    value: string
    ttl?: string | number
    status?: string
    priority?: number
    isRaw?: boolean
}

const DMARC_RECORD: DNSRecord = {
    record: 'DMARC',
    name: '_dmarc',
    type: 'TXT',
    value: 'v=DMARC1; p=none;',
    ttl: 'Auto',
    status: 'not_started',
    isRaw: true,
}

const createCopyToClipboard =
    (
        toast: ReturnType<typeof useToast>['toast'],
        t: (key: string) => string,
    ) =>
    async (value: string) => {
        await navigator.clipboard.writeText(value)
        toast({
            title: t('domainSettings.copied'),
            description: t('domainSettings.copiedDescription'),
        })
    }

const getDnsRecordStatus = (record: DNSRecord) => {
    if (record.isRaw) return 'bg-gray-100 text-gray-700'
    if (record.status === 'verified') return 'bg-green-100 text-green-700'
    return 'bg-yellow-100 text-yellow-700'
}

const getDnsRecordLabel = (record: DNSRecord, t: (key: string) => string) => {
    if (record.isRaw) return t('domainSettings.dnsStatus.manualSetup')
    return record.status || t('domainSettings.dnsStatus.notStarted')
}

interface DnsTableRowProps {
    record: DNSRecord
    showPriority: boolean
    onCopy: (value: string) => void
}

const DnsTableRow = React.memo<DnsTableRowProps>(({ record, showPriority, onCopy }) => {
    const { t } = useTranslation(['emails'])
    return (
        <tr className={record.isRaw ? 'bg-gray-50' : ''}>
            <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">{record.record}</td>
            <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">{record.type}</td>
            <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">{record.name}</td>
            <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">
                <button
                    className={`rounded px-2 py-1 text-left transition-colors hover:bg-gray-100 ${record.isRaw ? 'italic text-gray-600' : 'max-w-md truncate'
                        }`}
                    onClick={() => onCopy(record.value)}
                    title={record.value}
                >
                    {record.value}
                </button>
            </td>
            <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">{record.ttl || t('domainSettings.ttlAuto')}</td>
            {showPriority && (
                <td className="whitespace-nowrap px-4 py-2 font-mono text-sm">
                    {record.type === 'MX' ? record.priority : '-'}
                </td>
            )}
            <td className="whitespace-nowrap px-4 py-2 text-sm">
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getDnsRecordStatus(record)}`}>
                    {getDnsRecordLabel(record, t)}
                </span>
            </td>
        </tr>
    )
})

DnsTableRow.displayName = 'DnsTableRow'

interface DnsRecordsTableProps {
    records: DNSRecord[]
    onVerify: () => void
    isVerifying: boolean
}

const DnsRecordsTable = React.memo<DnsRecordsTableProps>(({ records, onVerify, isVerifying }) => {
    const { t } = useTranslation(['emails'])
    const { toast } = useToast()
    const allRecords = [...records, DMARC_RECORD]
    const showPriority = allRecords.some((r) => r.type === 'MX')
    const mxPriority = allRecords.find((r) => r.type === 'MX')?.priority
    const copyToClipboard = React.useCallback(createCopyToClipboard(toast, t), [toast, t])

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>{t('domainSettings.dnsRecords')}</CardTitle>
                        <p className="text-muted-foreground text-sm">
                            {t('domainSettings.dnsHelp')}
                        </p>
                    </div>
                    <Button variant="outline" onClick={onVerify} disabled={isVerifying}>
                        <ArrowPathIcon className="mr-2 h-4 w-4" />
                        {t('domainSettings.verify')}
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.record')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.type')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.name')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.value')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.ttl')}</th>
                                {showPriority && (
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.priority')}</th>
                                )}
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('domainSettings.table.status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {allRecords.map((record, index) => (
                                <DnsTableRow key={index} record={record} showPriority={showPriority} onCopy={copyToClipboard} />
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 rounded-md bg-blue-50 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                {t('domainSettings.notes')}
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>{t('domainSettings.note1')}</li>
                                    <li>{t('domainSettings.note2')}</li>
                                    <li>{t('domainSettings.note3')}</li>
                                    {mxPriority && (
                                        <li>
                                            {t('domainSettings.note4')} {mxPriority}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
})

DnsRecordsTable.displayName = 'DnsRecordsTable'

interface DomainFormFieldsProps {
    control: any
    errors: any
    domain: string
    currentDomain: any
    isPending: boolean
    onDomainChange: (value: string) => void
}

const DomainFormFields = React.memo<DomainFormFieldsProps>(
    ({ control, errors, domain, currentDomain, isPending, onDomainChange }) => {
        const { t } = useTranslation(['emails'])

        return (
            <>
                <div className="space-y-2">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="domain">
                            {t('domainSettings.customDomain')}
                        </Label>
                        <span className="text-red-500">*</span>
                    </div>
                    <Controller
                        name="domain"
                        control={control}
                        render={({ field }) => (
                            <Input
                                id="domain"
                                {...field}
                                onChange={(e) => onDomainChange(e.target.value)}
                                placeholder="email.yourdomain.com"
                                disabled={isPending || !!currentDomain}
                                error={!!errors.domain}
                            />
                        )}
                    />
                    {errors.domain && <p className="text-sm text-red-500">{errors.domain.message}</p>}
                    <p className="text-muted-foreground text-sm">
                        {currentDomain
                            ? t('domainSettings.domainLocked')
                            : t('domainSettings.domainHelp')}
                    </p>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="fromEmail">{t('domainSettings.fromEmail')}</Label>
                        <span className="text-red-500">*</span>
                    </div>
                    <Controller
                        name="fromEmail"
                        control={control}
                        render={({ field }) => (
                            <Input
                                id="fromEmail"
                                {...field}
                                placeholder={`noreply@${domain || 'yourdomain.com'}`}
                                disabled={isPending || !!currentDomain}
                                type="email"
                                error={!!errors.fromEmail}
                            />
                        )}
                    />
                    {errors.fromEmail && <p className="text-sm text-red-500">{errors.fromEmail.message}</p>}
                    <p className="text-muted-foreground text-sm">
                        {currentDomain
                            ? t('domainSettings.emailLocked')
                            : t('domainSettings.emailHelp')}
                    </p>
                </div>
            </>
        )
    }
)

DomainFormFields.displayName = 'DomainFormFields'

interface CurrentDomainCardProps {
    domain: any
    onVerify: () => void
    onRemove: () => void
    isVerifying: boolean
    isRemoving: boolean
}

const CurrentDomainCard = React.memo<CurrentDomainCardProps>(({ domain, onVerify, onRemove, isVerifying, isRemoving }) => {
    const { t } = useTranslation(['emails'])

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('domainSettings.currentDomain')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <Label>{t('domainSettings.domain')}</Label>
                            <p className="font-mono text-sm">{domain.domain}</p>
                        </div>
                        <div>
                            <Label>{t('domainSettings.fromEmailLabel')}</Label>
                            <p className="font-mono text-sm">{domain.fromEmail}</p>
                        </div>
                        <div>
                            <Label>{t('domainSettings.status')}</Label>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${domain.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}
                                >
                                    {domain.status}
                                </span>
                                {(isVerifying || isRemoving) && <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-600" />}
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onVerify} disabled={isVerifying || isRemoving}>
                            <ArrowPathIcon className="mr-2 h-4 w-4" />
                            {t('domainSettings.refresh')}
                        </Button>
                        <Button variant="destructive" onClick={onRemove} disabled={isVerifying || isRemoving}>
                            <TrashIcon className="mr-2 h-4 w-4" />
                            {t('domainSettings.remove')}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
})

CurrentDomainCard.displayName = 'CurrentDomainCard'

/**
 * Email Domain Settings Page
 * Manages custom email domain configuration for the store
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(EmailDomainSettingsPage, PERMISSIONS.STORES_MANAGE);

function EmailDomainSettingsPage() {
    const { t } = useTranslation(['emails', 'common'])
    const { id: storeId, storeRoutes } = useStoreNavigate()
    const [dnsRecords, setDnsRecords] = React.useState<DNSRecord[]>([])

    const methods = useForm<FormValues>({
        resolver: zodResolver(emailDomainSchema),
        defaultValues: {
            domain: '',
            fromEmail: '',
        },
    })

    const {
        handleSubmit,
        watch,
        setValue,
        control,
        formState: { errors },
    } = methods

    const domain = watch('domain')

    const { data: currentDomain, isLoading: isFetchingDomain } = useGetEmailDomain(storeId)
    const addDomainMutation = useAddEmailDomain()
    const checkDomainMutation = useCheckEmailDomain()
    const deleteDomainMutation = useDeleteEmailDomain()

    React.useEffect(() => {
        if (currentDomain?.domain) {
            setDnsRecords(currentDomain.domain.dnsRecords ?? [])
            setValue('domain', currentDomain.domain.domain)
            setValue('fromEmail', currentDomain.domain.fromEmail || '')
        }
    }, [currentDomain, setValue])

    const onSubmit = React.useCallback(
        (data: FormValues) => {
            addDomainMutation.mutate({
                ...data,
                storeId,
            })
        },
        [addDomainMutation, storeId]
    )

    const handleUpdateDomain = React.useCallback(
        (value: string) => {
            setValue('domain', value)
            const currentEmail = methods.getValues('fromEmail')
            if (currentEmail?.includes('@')) {
                const [localPart] = currentEmail.split('@')
                if (value) {
                    setValue('fromEmail', `${localPart}@${value}`)
                }
            }
        },
        [setValue, methods]
    )

    const handleVerify = React.useCallback(() => {
        checkDomainMutation.mutate({
            storeId,
        })
    }, [storeId, checkDomainMutation])

    const handleRemove = React.useCallback(() => {
        if (!currentDomain?.domain || !confirm(t('domainSettings.removeConfirm'))) {
            return
        }
        deleteDomainMutation.mutate(storeId)
    }, [currentDomain, storeId, deleteDomainMutation, t])

    const isPending =
        addDomainMutation.isPending || checkDomainMutation.isPending || deleteDomainMutation.isPending || isFetchingDomain
    const shouldShowDnsRecords = !!currentDomain?.domain

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                <div className="flex  flex-row items-center gap-4">
                    <BackButton to={storeRoutes.EMAILS()} />
                    <div>
                        <h1 className="text-xl font-semibold sm:text-2xl">
                            {t('domainSettings.title')}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {t('domainSettings.description')}
                        </p>
                    </div>
                </div>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {t('domainSettings.configuration')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DomainFormFields
                                control={control}
                                errors={errors}
                                domain={domain}
                                currentDomain={currentDomain?.domain}
                                isPending={isPending}
                                onDomainChange={handleUpdateDomain}
                            />

                            <div className="flex gap-2">
                                <Button type="submit" disabled={isPending || !!currentDomain?.domain}>
                                    {isPending
                                        ? t('domainSettings.saving')
                                        : currentDomain?.domain
                                            ? t('domainSettings.addAnother')
                                            : t('domainSettings.addDomain')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>


                </form>
            </FormProvider>
            {shouldShowDnsRecords && (

                <DnsRecordsTable
                    records={dnsRecords}
                    onVerify={handleVerify}
                    isVerifying={checkDomainMutation.isPending}
                />
            )}

            {currentDomain?.domain && (
                <CurrentDomainCard
                    domain={currentDomain.domain}
                    onVerify={handleVerify}
                    onRemove={handleRemove}
                    isVerifying={checkDomainMutation.isPending}
                    isRemoving={deleteDomainMutation.isPending}
                />
            )}
        </div>
    )
}

